import { useState, useCallback, useRef } from 'react';
import Map, { Source, Layer, NavigationControl, Popup } from 'react-map-gl/maplibre';
import type { MapLayerMouseEvent, MapRef } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { authenticatedMapRequest, INITIAL_VIEW, MAP_STYLE, territoryTileUrl } from '@/config';
import { useMapRecenter } from '@/hooks/useMapRecenter';
import { DataAttribution } from '@/components/map/DataAttribution';
import { useTerritoryStore } from '@/stores/territoryStore';

/**
 * HeatwaveMap — carte d'aide à la décision « Plan Canicule ».
 * Croise 3 couches sur la maille 200 m + refuges (cercles) + équipements sensibles (carrés).
 *
 * Layer active (single-select) :
 *   priority      = Exposition × Vulnérabilité × Déficit d'accès → liste hiérarchisée
 *   exposition    = indice ICU/LST
 *   vulnerabilite = 75+ INSEE + DPE F/G + densité
 *   acces         = déficit d'accès (minutes au refuge le plus proche)
 *
 * Accessibilité : refuges = cercles, équipements sensibles = symboles carrés (forme ≠ couleur).
 */
export type HeatLayer = 'priority' | 'exposition' | 'vulnerabilite' | 'acces';

const LAYER_CHIPS: { id: HeatLayer; label: string }[] = [
  { id: 'priority', label: 'Priorité' },
  { id: 'exposition', label: 'Exposition' },
  { id: 'vulnerabilite', label: 'Vulnérabilité' },
  { id: 'acces', label: 'Accès' },
];

const RAMP: Record<HeatLayer, unknown[]> = {
  priority: [
    'interpolate', ['linear'], ['get', 'priority'],
    0, '#ffe9c7', 0.4, '#ffba5a', 0.7, '#ff7a3c', 0.88, '#e1000f', 1, '#8a0b1a',
  ],
  exposition: [
    'interpolate', ['linear'], ['get', 'lst'],
    0, '#1f9d6b', 0.34, '#cbe04a', 0.55, '#ffd24a', 0.72, '#ff9f3c', 0.88, '#ff5a36', 1, '#cc1f1f',
  ],
  vulnerabilite: [
    'interpolate', ['linear'], ['get', 'vuln'],
    0, '#efe7f7', 0.5, '#b79bd8', 1, '#6b3fa0',
  ],
  acces: [
    'interpolate', ['linear'], ['get', 'access_min'],
    5, '#1f9d6b', 10, '#ffd24a', 18, '#e1000f',
  ],
};

export const HEAT_LEGEND: Record<HeatLayer, { title: string; lo: string; hi: string }> = {
  priority:      { title: 'Indice de priorité',        lo: 'faible',   hi: 'critique' },
  exposition:    { title: 'Exposition thermique (ICU)', lo: '+0 °C',    hi: '+7 °C' },
  vulnerabilite: { title: 'Vulnérabilité',              lo: 'faible',   hi: 'forte' },
  acces:         { title: "Déficit d'accès au frais",   lo: '≤ 5 min',  hi: '≥ 18 min' },
};

const LO_COLORS: Record<HeatLayer, string> = {
  priority:      '#ffe9c7',
  exposition:    '#1f9d6b',
  vulnerabilite: '#efe7f7',
  acces:         '#1f9d6b',
};
const HI_COLORS: Record<HeatLayer, string> = {
  priority:      '#8a0b1a',
  exposition:    '#cc1f1f',
  vulnerabilite: '#6b3fa0',
  acces:         '#e1000f',
};

function tooltipText(layer: HeatLayer, props: Record<string, number>): string {
  if (layer === 'exposition') return `+${(props.lst * 7).toFixed(1)} °C`;
  if (layer === 'vulnerabilite') return `vuln. ${Math.round(props.vuln * 100)}/100`;
  if (layer === 'acces') return `${Math.round(props.access_min)} min à pied`;
  return `priorité ${Math.round(props.priority * 100)}/100`;
}

interface HeatwaveMapProps {
  layer: HeatLayer;
  onLayerChange: (l: HeatLayer) => void;
  showAfter?: boolean;
  onShowAfterChange?: (v: boolean) => void;
  simDone?: boolean;
}

export function HeatwaveMap({
  layer,
  onLayerChange,
  showAfter = false,
  onShowAfterChange,
  simDone = false,
}: HeatwaveMapProps) {
  const [hover, setHover] = useState<{ lng: number; lat: number; text: string } | null>(null);
  const territoryCode = useTerritoryStore((s) => s.activeTerritory?.code_insee);
  const mapRef = useRef<MapRef>(null);
  const [mapReady, setMapReady] = useState(false);
  useMapRecenter(mapRef, mapReady);

  const onHover = useCallback(
    (e: MapLayerMouseEvent) => {
      const f = e.features?.[0];
      setHover(
        f
          ? {
              lng: e.lngLat.lng,
              lat: e.lngLat.lat,
              text: tooltipText(layer, f.properties as Record<string, number>),
            }
          : null,
      );
    },
    [layer],
  );

  const fillPaint = {
    'fill-color': RAMP[layer] as never,
    'fill-opacity': 0.62,
    'fill-outline-color': 'rgba(255,255,255,0.32)',
  };

  // After-simulation: reveal refuges that were opened
  const refugeFilter = showAfter
    ? (['any', ['==', ['get', 'permanent'], true], ['==', ['get', 'opened'], true]] as never)
    : (['==', ['get', 'permanent'], true] as never);

  const legend = HEAT_LEGEND[layer];

  if (!territoryCode) return null;

  return (
    <div className="flex flex-col gap-2 h-full">
      {/* Chips de couche + toggle Avant/Après */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {LAYER_CHIPS.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onLayerChange(c.id)}
            className={`rounded-full px-3 py-1 text-[12px] font-semibold border transition ${
              layer === c.id
                ? 'bg-ink text-white border-ink'
                : 'bg-white text-muted border-line hover:border-ink hover:text-ink'
            }`}
          >
            {c.label}
          </button>
        ))}
        {onShowAfterChange && (
          <div className="ml-auto flex rounded-[9px] border border-line overflow-hidden text-[12px] font-semibold">
            <button
              type="button"
              onClick={() => onShowAfterChange(false)}
              className={`px-3 py-1 transition ${!showAfter ? 'bg-ink text-white' : 'bg-white text-muted hover:text-ink'}`}
            >
              Avant
            </button>
            <button
              type="button"
              disabled={!simDone}
              onClick={() => onShowAfterChange(true)}
              className={`px-3 py-1 transition ${showAfter ? 'bg-[#1f8a5b] text-white' : 'bg-white text-muted hover:text-ink'} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              Après
            </button>
          </div>
        )}
      </div>

      {/* Carte */}
      <div className="relative flex-1 min-h-0 overflow-hidden rounded-panel border border-line">
        <Map
          ref={mapRef}
          initialViewState={INITIAL_VIEW}
          mapStyle={MAP_STYLE}
          transformRequest={authenticatedMapRequest}
          style={{ width: '100%', height: '100%' }}
          interactiveLayerIds={['heat-fill']}
          onLoad={() => setMapReady(true)}
          onMouseMove={onHover}
        >
          <NavigationControl position="top-right" showCompass={false} />
          <DataAttribution />

          {/* Choroplèthe de la couche active */}
          <Source
            id="heat"
            type="vector"
            tiles={[territoryTileUrl(territoryCode, 'heatwave_grid')]}
            minzoom={10}
            maxzoom={16}
          >
            <Layer id="heat-fill" type="fill" source-layer="heatwave_grid" paint={fillPaint} />
          </Source>

          {/* Isochrones piéton 5/10 min des refuges */}
          <Source
            id="iso"
            type="vector"
            tiles={[territoryTileUrl(territoryCode, 'cool_isochrones')]}
            minzoom={11}
            maxzoom={16}
          >
            <Layer
              id="iso10"
              type="fill"
              source-layer="cool_isochrones"
              filter={['==', ['get', 'minutes'], 10] as never}
              paint={{ 'fill-color': '#1f9d6b', 'fill-opacity': 0.06 }}
            />
            <Layer
              id="iso10-line"
              type="line"
              source-layer="cool_isochrones"
              filter={['==', ['get', 'minutes'], 10] as never}
              paint={{ 'line-color': '#1f9d6b', 'line-opacity': 0.32, 'line-dasharray': [2, 2] }}
            />
            <Layer
              id="iso5"
              type="fill"
              source-layer="cool_isochrones"
              filter={['==', ['get', 'minutes'], 5] as never}
              paint={{ 'fill-color': '#1f9d6b', 'fill-opacity': 0.1 }}
            />
          </Source>

          {/* Refuges — cercles (différenciés de sensitives par la FORME) */}
          <Source
            id="refuges"
            type="vector"
            tiles={[territoryTileUrl(territoryCode, 'cool_refuges')]}
            minzoom={11}
            maxzoom={18}
          >
            <Layer
              id="refuges-pt"
              type="circle"
              source-layer="cool_refuges"
              filter={refugeFilter}
              paint={{
                'circle-radius': 7,
                'circle-color': '#ffffff',
                'circle-stroke-width': 2.5,
                'circle-stroke-color': ['case', ['get', 'climatise'], '#2563eb', '#1f8a5b'] as never,
              }}
            />
          </Source>

          {/* Équipements sensibles — carrés via text-field '■' (EHPAD/crèches/écoles).
               Sprites 'facility-red'/'facility-amber' non embarqués dans le style IGN :
               on utilise un caractère Unicode carré pour distinguer la forme des refuges
               (cercles) sans charger d'image externe. */}
          <Source
            id="sensitive"
            type="vector"
            tiles={[territoryTileUrl(territoryCode, 'sensitive_facilities')]}
            minzoom={11}
            maxzoom={18}
          >
            <Layer
              id="sensitive-sq"
              type="symbol"
              source-layer="sensitive_facilities"
              layout={{
                'text-field': '■',
                'text-size': 18,
                'text-allow-overlap': true,
                'text-ignore-placement': true,
                'text-anchor': 'center',
              }}
              paint={{
                'text-color': ['case', ['get', 'critical'], '#e1000f', '#e08600'] as never,
              }}
            />
          </Source>

          {hover && (
            <Popup longitude={hover.lng} latitude={hover.lat} closeButton={false} offset={12}>
              <div className="font-sans text-xs">
                <div className="font-bold text-ink">{legend.title}</div>
                <div className="font-mono text-muted">{hover.text}</div>
              </div>
            </Popup>
          )}
        </Map>
      </div>

      {/* Légende dynamique */}
      <div className="flex items-center gap-2 px-1">
        <span className="h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ background: LO_COLORS[layer] }} />
        <span className="text-[11px] text-muted">{legend.lo}</span>
        <div className="flex-1 h-[5px] rounded-full" style={{
          background: `linear-gradient(to right, ${LO_COLORS[layer]}, ${HI_COLORS[layer]})`,
        }} />
        <span className="text-[11px] text-muted">{legend.hi}</span>
        <span className="h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ background: HI_COLORS[layer] }} />
      </div>

      {/* Symbologie */}
      <div className="flex items-center gap-4 px-1 text-[11px] text-muted">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-full border-2 border-[#2563eb] bg-white" aria-hidden="true" />
          Refuge climatisé
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-full border-2 border-[#1f8a5b] bg-white" aria-hidden="true" />
          Refuge parc/eau
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-[2px] bg-[#e1000f]" aria-hidden="true" />
          Éq. sensible critique
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-[2px] bg-[#e08600]" aria-hidden="true" />
          Éq. sensible surveillé
        </span>
      </div>
    </div>
  );
}
